/**
 * Frontend Profile Migration Utility
 * Handles migration of existing profile data to the unified memory system
 */

import { authFetch } from './auth';

/**
 * Checks if a user's profile needs migration to memory system
 * @param {object} profile - User profile data
 * @returns {boolean} - Whether migration is needed
 */
export function needsProfileMigration(profile) {
  if (!profile) return false;

  // Check if user has completed the old wizard but hasn't been migrated
  const hasOldWizardData = 
    profile.profileComplete &&
    (profile.child?.birthYear ||
     profile.kommune ||
     profile.schoolStage ||
     (profile.challenges && profile.challenges.length > 0) ||
     (profile.currentBenefits && profile.currentBenefits.length > 0));

  // Check if migration flag exists (this will be set after successful migration)
  const alreadyMigrated = profile.memorySystemMigrated;

  return hasOldWizardData && !alreadyMigrated;
}

/**
 * Performs profile migration to memory system
 * @param {object} profile - User profile data
 * @returns {Promise<object>} - Migration result
 */
export async function migrateProfileToMemories(profile) {
  try {
    const response = await authFetch('/memories/migrate-profile', {
      method: 'POST',
      // Background call: a 402 (user without an active subscription) must
      // not hard-navigate the page to /abonnement — this runs on every
      // profile load, including on gate-exempt pages like /account and
      // /admin. authFetch still throws; the catch below swallows it.
      redirectOnGate: false,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        profileData: profile
      })
    });

    if (response.ok) {
      const result = await response.json();
      
      // If migration was successful, update the profile to mark as migrated
      if (result.success && result.migrationNeeded && !result.alreadyMigrated) {
        await markProfileAsMigrated();
      }
      
      return result;
    } else {
      console.error('Profile migration failed:', await response.text());
      return {
        success: false,
        message: 'Migration request failed'
      };
    }
  } catch (error) {
    console.error('Error during profile migration:', error);
    return {
      success: false,
      message: 'Migration error',
      error: error.message
    };
  }
}

/**
 * Marks profile as migrated to prevent duplicate migrations
 * @returns {Promise<boolean>} - Whether marking was successful
 */
async function markProfileAsMigrated() {
  try {
    const response = await authFetch('/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        memorySystemMigrated: true,
        migrationDate: new Date().toISOString()
      })
    });

    return response.ok;
  } catch (error) {
    console.error('Error marking profile as migrated:', error);
    return false;
  }
}

/**
 * Automatically handles profile migration on login/profile load
 * Should be called from ProfileContext or main app initialization
 * @param {object} profile - User profile data
 * @param {function} onMigrationComplete - Optional callback when migration completes
 * @returns {Promise<object|null>} - Migration result or null if no migration needed
 */
export async function handleAutoMigration(profile, onMigrationComplete = null) {
  if (!needsProfileMigration(profile)) {
    return null;
  }

  const migrationResult = await migrateProfileToMemories(profile);

  if (migrationResult.success) {
    if (onMigrationComplete) {
      onMigrationComplete(migrationResult);
    }
  } else {
    console.error('Profile migration failed:', migrationResult.message);
  }
  
  return migrationResult;
}

/**
 * Shows a user-friendly notification about migration
 * @param {object} migrationResult - Result from migration
 * @returns {object} - Notification data for UI
 */
export function getMigrationNotification(migrationResult) {
  if (!migrationResult || !migrationResult.success) {
    return null;
  }

  if (migrationResult.alreadyMigrated) {
    return null; // Don't show notification for already migrated users
  }

  if (!migrationResult.migrationNeeded) {
    return null; // Don't show notification if no migration was needed
  }

  const memoriesCount = migrationResult.memoriesCreated || 0;
  
  return {
    type: 'success',
    title: 'Profil oppgradert!',
    message: `Din profil er nå koblet til det nye minnesystemet. ${memoriesCount} minner ble opprettet for bedre personalisering.`,
    duration: 8000, // Show for 8 seconds
    dismissible: true,
    icon: '🧠'
  };
}

// Note: React hook is commented out to avoid ESLint errors in utility file
// Uncomment and import React hooks if needed in a React component context

/*
import { useState, useEffect } from 'react';

export function useProfileMigration(profile, profileLoaded = false) {
  const [migrationState, setMigrationState] = useState({
    isChecking: false,
    isRunning: false,
    completed: false,
    result: null
  });

  useEffect(() => {
    if (!profileLoaded || !profile) return;

    const runMigration = async () => {
      setMigrationState(prev => ({ ...prev, isChecking: true }));
      
      if (needsProfileMigration(profile)) {
        setMigrationState(prev => ({ ...prev, isRunning: true }));
        
        const result = await migrateProfileToMemories(profile);
        
        setMigrationState({
          isChecking: false,
          isRunning: false,
          completed: true,
          result
        });
      } else {
        setMigrationState(prev => ({ 
          ...prev, 
          isChecking: false,
          completed: true 
        }));
      }
    };

    runMigration();
  }, [profile, profileLoaded]);

  return {
    needsMigration: needsProfileMigration(profile),
    ...migrationState
  };
}
*/